import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Anchor, Globe2, ShieldCheck, Zap } from "lucide-react";
import { HeroCanvas } from "../components/HeroCanvas";
import { Reveal } from "../components/Reveal";
import { COMPANY, INDUSTRIES, MANIFESTO, SERVICES } from "../data/content";

const heroLines = ["CLACH GLOBAL", "SERVICES", "...IGNITING THE WORLD"];
const stats = [
  { value: "60+", label: "Combined years across the value chain" },
  { value: "05", label: "Integrated service practices" },
  { value: "04", label: "Strategic industries served" },
];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      className="block"
    >
      {children}
    </motion.span>
  </span>
);

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const rigY = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <main data-testid="home-page" className="overflow-hidden bg-[#070C14] text-white">
      <section ref={heroRef} data-testid="home-hero" className="relative min-h-screen px-4 pb-24 pt-32 sm:px-8 lg:px-10 lg:pt-40">
        <HeroCanvas />
        <div className="absolute inset-0 hero-grid opacity-45" aria-hidden="true" />
        <div className="absolute left-1/2 top-0 h-96 w-96 -translate-x-1/2 rounded-full bg-blue-500/15 blur-3xl" aria-hidden="true" />

        <div className="relative mx-auto grid max-w-7xl items-center gap-16 lg:grid-cols-[1.05fr_.95fr]">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="font-mono text-xs uppercase tracking-[0.34em] text-amber-400"
              data-testid="hero-eyebrow"
            >
              Energy · Oil & Gas · Environment · Maritime
            </motion.p>
            <h1 className="mt-7 font-heading text-5xl font-bold uppercase leading-[0.95] tracking-tight sm:text-7xl lg:text-8xl" data-testid="hero-title">
              {heroLines.map((line, index) => (
                <MaskedLine key={line} delay={0.16 + index * 0.14}>
                  <span className={index === 2 ? "text-amber-400" : ""}>{line}</span>
                </MaskedLine>
              ))}
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.72 }}
              className="mt-8 max-w-2xl text-base leading-8 text-slate-300 sm:text-lg"
              data-testid="hero-description"
            >
              {COMPANY.name} is an innovative resource and solutions provider serving niche sectors of the Nigerian oil and gas market with global connectivity, customized solutions, responsiveness, and evident price-value advantage.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.86 }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <Link to="/services" data-testid="hero-explore-services-button" className="group inline-flex items-center gap-3 rounded-full bg-amber-400 px-7 py-4 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-[#111827] transition-all duration-300 hover:bg-white">
                Explore services <ArrowUpRight size={17} className="transition-transform group-hover:rotate-45" />
              </Link>
              <Link to="/about" data-testid="hero-company-profile-button" className="inline-flex items-center gap-3 rounded-full border border-white/15 px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-blue-400 hover:text-blue-300">
                Company profile
              </Link>
            </motion.div>
          </div>

          <div className="relative min-h-[520px]" data-testid="hero-visual-composition">
            <motion.div style={{ y: imageY }} className="spotlight clip-frame absolute right-0 top-4 h-[430px] w-[88%] overflow-hidden border border-white/10">
              <img src="/assets/page_1_img_1.jpeg" alt="Industrial oil and gas valves" className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#070C14]/65 via-transparent to-blue-500/10" />
            </motion.div>
            <motion.div style={{ y: rigY }} className="clip-frame-alt absolute bottom-4 left-0 h-72 w-[58%] overflow-hidden border border-amber-400/30 shadow-2xl shadow-blue-950/60">
              <img src="/assets/page_2_img_1.jpeg" alt="Offshore oil rig at sunset" className="h-full w-full object-cover" />
            </motion.div>
            <div className="absolute bottom-24 right-4 max-w-[220px] rounded-3xl border border-white/10 bg-white/10 p-5 backdrop-blur-xl" data-testid="hero-value-card">
              <ShieldCheck className="text-amber-400" size={24} />
              <p className="mt-4 font-heading text-xl font-semibold">Safety, speed, and specification certainty.</p>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="editorial-marquee" className="border-y border-white/10 bg-[#0D131F] py-5">
        <div className="marquee-track font-heading text-2xl font-bold uppercase tracking-[0.18em] text-slate-500">
          {[0, 1].map((copy) => (
            <div key={copy} className="marquee-copy" aria-hidden={copy === 1}>
              {INDUSTRIES.concat(SERVICES.map((service) => service.title)).map((item) => (
                <span key={`${copy}-${item}`} className="mx-8 inline-flex items-center gap-8">
                  {item} <span className="h-2 w-2 rounded-full bg-amber-400" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      <section data-testid="manifesto-section" className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-10 lg:py-32">
        <Reveal>
          <div className="mb-14 flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.32em] text-blue-400">Operating manifesto</p>
              <h2 className="mt-4 max-w-2xl font-heading text-3xl font-bold tracking-tight sm:text-5xl">Precision principles for complex energy work.</h2>
            </div>
            <p className="max-w-md text-sm leading-7 text-slate-400">Four commitments extracted from Clach's corporate profile and rebuilt as a modern digital charter.</p>
          </div>
        </Reveal>
        <div className="grid gap-px overflow-hidden rounded-3xl border border-white/10 bg-white/10 md:grid-cols-2 lg:grid-cols-4">
          {MANIFESTO.map((chapter, index) => (
            <Reveal key={chapter.number} delay={index * 0.08} className="group bg-[#0D131F] p-8 transition-colors duration-500 hover:bg-[#141E33]">
              <p className="font-mono text-sm text-amber-400">{chapter.number}</p>
              <h3 className="mt-12 font-heading text-2xl font-semibold">{chapter.title}</h3>
              <p className="mt-5 text-sm leading-7 text-slate-400">{chapter.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="home-services-preview" className="mx-auto max-w-7xl px-4 pb-24 sm:px-8 lg:px-10 lg:pb-32">
        <Reveal className="mb-12 flex items-end justify-between gap-8">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.32em] text-blue-400">Services</p>
            <h2 className="mt-4 font-heading text-3xl font-bold sm:text-5xl">Five disciplines. One delivery web.</h2>
          </div>
          <Link to="/services" data-testid="view-all-services-link" className="hidden items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-amber-300 sm:flex">
            View all <ArrowUpRight size={16} />
          </Link>
        </Reveal>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-5">
          {SERVICES.map((service, index) => {
            const Icon = service.icon;
            return (
              <Reveal key={service.slug} delay={index * 0.06}>
                <Link to={`/services/${service.slug}`} data-testid={`home-service-${service.slug}`} className="group block overflow-hidden rounded-3xl border border-white/10 bg-[#0D131F] transition-all duration-500 hover:-translate-y-2 hover:border-blue-400/40">
                  <div className="h-44 overflow-hidden">
                    <img src={service.image} alt={service.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </div>
                  <div className="p-6">
                    <Icon className="text-amber-400" size={22} />
                    <h3 className="mt-5 font-heading text-xl font-semibold">{service.title}</h3>
                    <p className="mt-3 line-clamp-3 text-sm leading-6 text-slate-400">{service.summary}</p>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section data-testid="home-capability-band" className="relative overflow-hidden border-t border-white/10 px-4 py-24 sm:px-8 lg:px-10">
        <img src="/assets/page_18_img_1.jpeg" alt="Offshore production platform" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#070C14] via-[#070C14]/88 to-[#070C14]/35" />
        <div className="relative mx-auto grid max-w-7xl gap-12 lg:grid-cols-[.8fr_1.2fr]">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.32em] text-amber-400">Global connectivity</p>
            <h2 className="mt-4 font-heading text-3xl font-bold sm:text-5xl">Built for upstream pressure and downstream pace.</h2>
          </Reveal>
          <div className="grid gap-6 sm:grid-cols-3">
            {[
              { icon: Zap, title: "Energy" },
              { icon: Anchor, title: "Maritime" },
              { icon: Globe2, title: "Global trade" },
            ].map((item, index) => (
              <Reveal key={item.title} delay={index * 0.08} className="rounded-3xl border border-white/10 bg-white/10 p-6 backdrop-blur-xl">
                <item.icon className="text-blue-400" size={26} />
                <h3 className="mt-8 font-heading text-2xl font-semibold">{item.title}</h3>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
